'use client';

import { forwardRef, ReactNode, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { Loading } from 'ui-components/common/Loading';

const THRESHOLD = 0.5;

interface InfiniteScrollWrapperProps {
  children: ReactNode;
  showLoading?: boolean;
  hasNext?: boolean;
  loadMore?: () => Promise<any>;
  className?: string;
}

export const InfiniteScrollWrapper = forwardRef(
  (
    { className, showLoading = false, children, hasNext, loadMore }: InfiniteScrollWrapperProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleObserver = useCallback(
      async ([entry]: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        if (!hasNext) {
          observer.unobserve(entry.target);
          return;
        }

        if (entry.isIntersecting) {
          setIsLoading(true);
          observer.unobserve(entry.target);
          await loadMore?.();
          observer.observe(entry.target);
          setIsLoading(false);
        }
      },
      [hasNext, loadMore],
    );

    useEffect(() => {
      if (!('IntersectionObserver' in window)) {
        console.debug('Intersection Observer API is not available');
        return undefined;
      }

      let observer: IntersectionObserver | undefined;
      if (innerRef?.current) {
        observer = new IntersectionObserver(handleObserver, { threshold: THRESHOLD });
        observer.observe(innerRef.current);
      }

      return () => {
        if (observer) {
          observer.disconnect();
        }
      };
    }, [handleObserver]);

    return (
      <div className={className} ref={ref}>
        {children}
        {showLoading && isLoading && <Loading />}
        <div ref={innerRef} />
      </div>
    );
  },
);

InfiniteScrollWrapper.displayName = 'InfiniteScrollWrapper';
